import { Camera as BJSCamera } from "@babylonjs/core/Cameras/camera";
import { TargetCamera } from "@babylonjs/core/Cameras/targetCamera";
import { Engine } from "@babylonjs/core/Engines/engine";
import { Effect } from "@babylonjs/core/Materials/effect";
import { Material } from "@babylonjs/core/Materials/material";
import { ShaderMaterial } from "@babylonjs/core/Materials/shaderMaterial";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { RenderTargetTexture } from "@babylonjs/core/Materials/Textures/renderTargetTexture";
import { Texture as BJSTexture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { PlaneBuilder } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { InstancedMesh } from "@babylonjs/core/Meshes/instancedMesh";
import { LinesMesh } from "@babylonjs/core/Meshes/linesMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { PostProcess } from "@babylonjs/core/PostProcesses/postProcess";
import { eq, PI_DIV2 } from "../../utils/math";
import { BoundingBox } from "../mdl/BoundingBox";
import { removeFromScene, updateFrozenWorldMatrix } from "../utils/MeshUtils";
import { CameraControl, CameraModeMask } from "../view/CameraControl";
import { ShadowGenerator } from "./ShadowGenerator";
var ShadowPlane = /** @class */ (function () {
    function ShadowPlane(pApp, pResolution) {
        var _this = this;
        if (pResolution === void 0) { pResolution = 1024; }
        this.onModelModified = function () {
            _this.mShadowsNeedUpdate = true;
        };
        this.onViewerBeforeRender = function () {
            _this.updateShadows();
        };
        this.mApp = pApp;
        this.mScene = pApp.scene;
        this.mShadowsNeedUpdate = false;
        this.mEnabled = true;
        this.mShadowUpdateTimeout = null;
        var tCamViewingRange = 0.25;
        this.mMinPlaneSize = (pResolution / 4096) * 10; //(512 = 1.25, 1024 = 2.5, 2048 = 5.0, 4096 = 10.0)
        this.mPlaneSize = this.mMinPlaneSize;
        this.mPosY = 0;
        this.mTransparency = 0.7;
        this.createCamera(tCamViewingRange);
        this.createRenderTarget(pResolution);
        this.createPlane();
        //register events
        this.mApp.model.eventModified.addListener(this.onModelModified);
        this.mApp.viewer.eventBeforeRender.addListener(this.onViewerBeforeRender);
    }
    Object.defineProperty(ShadowPlane.prototype, "planeYOffset", {
        get: function () {
            return (this.mPlane.position.y - this.mPosY);
        },
        set: function (pOffset) {
            this.mPlane.position.y = this.mPosY + pOffset;
            updateFrozenWorldMatrix(this.mPlane);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ShadowPlane.prototype, "planeYPosition", {
        get: function () {
            return (this.mPosY);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ShadowPlane.prototype, "transparency", {
        get: function () {
            return (this.mTransparency);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ShadowPlane.prototype, "enabled", {
        get: function () {
            return (this.mEnabled);
        },
        enumerable: false,
        configurable: true
    });
    ShadowPlane.prototype.dispose = function () {
        this.mPlane.dispose();
        this.mPlaneMaterial.dispose();
        this.mCamera.dispose();
        this.mRenderTarget.dispose();
        this.mDepthMaterial.dispose();
        this.mDummyMesh.dispose();
        this.mApp.model.eventModified.removeListener(this.onModelModified);
        this.mApp.viewer.eventBeforeRender.removeListener(this.onViewerBeforeRender);
        this.mShadowsNeedUpdate = false;
    };
    ShadowPlane.prototype.setEnabled = function (pEnabled) {
        if (this.mEnabled === pEnabled)
            return;
        this.mPlane.isVisible = pEnabled;
        this.mApp.viewer.requestRenderFrame();
    };
    ShadowPlane.prototype.setPlaneYPosition = function (pPos) {
        if (this.mPosY === pPos)
            return;
        var tOffset = this.planeYOffset;
        this.mPosY = pPos;
        this.mCamera.position.y = this.mPosY + ShadowPlane.CAMERA_Y_OFFSET;
        this.mPlane.position.y = this.mPosY + tOffset;
        updateFrozenWorldMatrix(this.mPlane);
        this.mShadowsNeedUpdate = true;
        this.mApp.viewer.requestRenderFrame();
    };
    ShadowPlane.prototype.setTransparency = function (pTransparency) {
        if (this.mTransparency === pTransparency)
            return;
        this.mTransparency = pTransparency;
        this.mShadowsNeedUpdate = true;
        this.mApp.viewer.requestRenderFrame();
    };
    ShadowPlane.prototype.notifyModelInteraction = function () {
        var _this = this;
        if (!this.mEnabled)
            return;
        this.mPlane.isVisible = false;
        this.clearUpdateTimeout();
        this.mShadowUpdateTimeout = window.setTimeout(function () {
            _this.mShadowUpdateTimeout = null;
            _this.mShadowsNeedUpdate = true;
            _this.mApp.viewer.requestRenderFrame();
        }, 250);
    };
    ShadowPlane.prototype.prepareRenderFrame = function (pMultiSamplingStep, pMaxSteps) {
    };
    ShadowPlane.prototype.finalizeRenderFrame = function (pMultiSamplingStep, pMaxSteps) {
    };
    ShadowPlane.prototype.clearUpdateTimeout = function () {
        if (this.mShadowUpdateTimeout != null) {
            clearTimeout(this.mShadowUpdateTimeout);
            this.mShadowUpdateTimeout = null;
        }
    };
    ShadowPlane.prototype.addMeshToRenderList = function (pNode, pRenderList) {
        if (pNode == null || pNode instanceof LinesMesh)
            return;
        if (pNode instanceof Mesh && pNode.geometry != null || pNode instanceof InstancedMesh) {
            if (ShadowGenerator.isMeshShadowDisabled(pNode))
                return;
            var tBInfo = pNode.getBoundingInfo();
            //ignore objects which are positioned at y = this.mPosY and have zero height (eg. planes)
            if (tBInfo != null && (!eq(tBInfo.boundingBox.minimumWorld.y, this.mPosY) ||
                !eq(tBInfo.boundingBox.maximumWorld.y, this.mPosY))) {
                pRenderList.push(pNode);
            }
        }
    };
    ShadowPlane.prototype.updateShadows = function () {
        if (!this.mEnabled || !this.mShadowsNeedUpdate)
            return;
        this.clearUpdateTimeout();
        this.mShadowsNeedUpdate = false;
        this.mPlane.isVisible = true; //maybe was disabled in notifyModelInteraction()
        //update renderlist
        var tRenderList = new Array();
        tRenderList.push(this.mDummyMesh);
        for (var _i = 0, _a = this.mApp.model.elements; _i < _a.length; _i++) {
            var tElement = _a[_i];
            if (tElement.node != null) {
                this.addMeshToRenderList(tElement.node, tRenderList);
                var tChilds = tElement.node.getChildTransformNodes(false);
                for (var _b = 0, tChilds_1 = tChilds; _b < tChilds_1.length; _b++) {
                    var tMesh = tChilds_1[_b];
                    this.addMeshToRenderList(tMesh, tRenderList);
                }
            }
        }
        this.mRenderTarget.renderList = tRenderList;
        this.updatePlaneSize();
        //render shadow texture
        var tPrevCamera = this.mScene.activeCamera;
        this.mScene.activeCamera = this.mCamera;
        this.mScene.incrementRenderId();
        this.mScene.resetCachedMaterial();
        this.mScene.updateTransformMatrix(true);
        this.mRenderTarget.render(true);
        this.mScene.activeCamera = tPrevCamera;
    };
    ShadowPlane.prototype.createCamera = function (pViewingRange) {
        this.mCamera = new TargetCamera("shadowPlaneCamera", new Vector3(0, this.mPosY + ShadowPlane.CAMERA_Y_OFFSET, 0), this.mScene);
        this.mCamera.rotation.x = -PI_DIV2;
        this.mCamera.upVector = new Vector3(0, 0, -1);
        this.mCamera.mode = BJSCamera.ORTHOGRAPHIC_CAMERA;
        this.mCamera.minZ = 0;
        this.mCamera.maxZ = pViewingRange;
    };
    ShadowPlane.prototype.createRenderTarget = function (pTextureSize) {
        var _this = this;
        var tTexelSize = 1.0 / pTextureSize;
        this.mRenderTarget = new RenderTargetTexture("shadowPlane", pTextureSize, this.mScene, true, true, Engine.TEXTURETYPE_UNSIGNED_INT, false, BJSTexture.TRILINEAR_SAMPLINGMODE, false, false);
        this.mRenderTarget.clearColor = new Color4(1, 1, 1, 1);
        this.mRenderTarget.anisotropicFilteringLevel = this.mApp.engine.getCaps().maxAnisotropy;
        this.mRenderTarget.hasAlpha = true;
        var tPostProcess = new PostProcess("shadowPlaneDof", "wcf.core.ShadowPlaneDof", ["dofAmount", "transparency"], [], { width: pTextureSize, height: pTextureSize }, this.mCamera);
        tPostProcess.onApply = function (pEffect) {
            //reduce dof amount with increasing plane size
            var tRatio = _this.mPlaneSize / pTextureSize;
            var tAmount = 12.0 * (ShadowPlane.REFERENCE_RATIO / tRatio);
            pEffect.setFloat("dofAmount", tAmount * tTexelSize);
            pEffect.setFloat("transparency", _this.mTransparency);
        };
        tPostProcess = new PostProcess("shadowPlaneBlur", "wcf.core.ShadowPlaneBlur", ["blurAmount"], [], { width: pTextureSize, height: pTextureSize }, this.mCamera);
        tPostProcess.onApply = function (pEffect) {
            pEffect.setFloat("blurAmount", 0.75 * tTexelSize);
        };
        //dummy mesh is used to ensure that the render target will update
        //if a render target has an empty renderList it will not render
        this.mDummyMesh = new Mesh("shadowPlaneDummy", this.mScene);
        this.mDummyMesh.freezeWorldMatrix();
        this.mDummyMesh.isPickable = false;
        this.mDummyMesh.isVisible = false;
        this.mDummyMesh.setEnabled(false);
        removeFromScene(this.mDummyMesh, this.mScene);
        //create depth material
        this.mDepthMaterial = new ShaderMaterial("shadowPlaneDepth", this.mScene, "wcf.core.ShadowPlaneDepth", {
            attributes: ["position", "world0", "world1", "world2", "world3"],
            uniforms: ["viewProjection", "world"]
        });
        this.mDepthMaterial.backFaceCulling = false;
        this.mDepthMaterial.freeze(); //do not freeze the material if instances are used, to allow switching between instances and non-instances
        this.mRenderTarget.onBeforeRender = function () {
            //override mesh materials with mDepthMaterial
            for (var _i = 0, _a = _this.mRenderTarget.renderList; _i < _a.length; _i++) {
                var tMesh = _a[_i];
                if (!(tMesh instanceof InstancedMesh)) {
                    tMesh._savedMaterial = tMesh.material;
                    tMesh.material = _this.mDepthMaterial;
                }
            }
        };
        this.mRenderTarget.onAfterRender = function () {
            //restore original material
            for (var _i = 0, _a = _this.mRenderTarget.renderList; _i < _a.length; _i++) {
                var tMesh = _a[_i];
                if (!(tMesh instanceof InstancedMesh)) {
                    tMesh.material = tMesh._savedMaterial;
                }
            }
        };
    };
    ShadowPlane.prototype.createPlane = function () {
        //internet explorer does not reset the state correctly and the zOffset would be applied to all materials
        var tIsInternetExplorer = (/(MSIE|Trident)/).test(navigator.userAgent);
        var tZoffset = tIsInternetExplorer ? 0 : -0.01;
        this.mPlaneMaterial = new PBRMaterial("shadowPlane", this.mScene);
        this.mPlaneMaterial.albedoColor = Color3.White();
        this.mPlaneMaterial.sideOrientation = Material.ClockWiseSideOrientation;
        this.mPlaneMaterial.albedoTexture = this.mRenderTarget;
        this.mPlaneMaterial.useAlphaFromAlbedoTexture = true;
        this.mPlaneMaterial.disableLighting = true;
        this.mPlaneMaterial.unlit = true;
        this.mPlaneMaterial.zOffset = tZoffset;
        this.mPlaneMaterial.freeze();
        this.mPlane = PlaneBuilder.CreatePlane("shadowPlane", { width: 1.0, height: 1.0 }, this.mScene);
        this.mPlane.position.y = this.mPosY + 0.0005; //add additional 0.5mm offset to prevent z-fighting
        this.mPlane.rotation.x = -PI_DIV2;
        this.mPlane.isPickable = false;
        this.mPlane.material = this.mPlaneMaterial;
        this.mPlane.freezeWorldMatrix();
        //only visiblie in ortho and perspective views
        CameraControl.SetMeshCameraModeMask(this.mPlane, CameraModeMask.Perspective | CameraModeMask.Orthographic);
    };
    ShadowPlane.prototype.updatePlaneSize = function () {
        var tTotalBounds = new BoundingBox();
        for (var _i = 0, _a = this.mRenderTarget.renderList; _i < _a.length; _i++) {
            var tMesh = _a[_i];
            if (tMesh === this.mDummyMesh)
                continue;
            var tBInfo = tMesh.getBoundingInfo();
            if (tBInfo != null) {
                tTotalBounds.expandByPointInPlace(tBInfo.boundingBox.minimumWorld);
                tTotalBounds.expandByPointInPlace(tBInfo.boundingBox.maximumWorld);
            }
        }
        if (tTotalBounds.isValid()) {
            var tCenter = tTotalBounds.getCenter();
            this.mPlaneSize = Math.max(tTotalBounds.max.x - tTotalBounds.min.x, tTotalBounds.max.z - tTotalBounds.min.z);
            if (this.mPlaneSize < this.mMinPlaneSize)
                this.mPlaneSize = this.mMinPlaneSize;
            this.mPlaneSize += 0.1; //add offset for blurring
            this.mPlane.scaling.x = this.mPlaneSize;
            this.mPlane.scaling.y = this.mPlaneSize;
            this.mPlane.position.x = tCenter.x;
            this.mPlane.position.z = tCenter.z;
            var tOrtho = this.mPlaneSize * 0.5;
            this.mCamera.orthoTop = tOrtho;
            this.mCamera.orthoBottom = -tOrtho;
            this.mCamera.orthoLeft = -tOrtho;
            this.mCamera.orthoRight = tOrtho;
            this.mCamera.position.x = tCenter.x;
            this.mCamera.position.z = tCenter.z;
            updateFrozenWorldMatrix(this.mPlane);
        }
    };
    //// private ///////////////////////////////////////////////////////////////////////////////
    ShadowPlane.REFERENCE_RATIO = 10.0 / 1024.0;
    ShadowPlane.CAMERA_Y_OFFSET = -0.0005;
    return ShadowPlane;
}());
export { ShadowPlane };
//register shaders
Effect.ShadersStore["wcf.core.ShadowPlaneDepthVertexShader"] = "\nprecision highp float;\n\n#include<instancesDeclaration>\nattribute vec3 position;\nuniform mat4 viewProjection;\n\nvoid main(void)\n{\n    #include<instancesVertex>\n    gl_Position = viewProjection * finalWorld * vec4(position, 1.0);\n}";
Effect.ShadersStore["wcf.core.ShadowPlaneDepthFragmentShader"] = "\nprecision highp float;\n\nvoid main(void)\n{\n    float depth = gl_FragCoord.z;\n    gl_FragColor = vec4(depth, depth, depth, 1.0);\n}";
Effect.ShadersStore["wcf.core.ShadowPlaneBlurFragmentShader"] = "\nprecision highp float;\n\nvarying vec2 vUV;\nuniform sampler2D textureSampler;\nuniform float blurAmount;\n\nvoid main(void)\n{\n    float alpha = 0.0;\n    alpha += texture2D(textureSampler, vUV + vec2(-1.0, -1.0) * blurAmount).a;\n    alpha += texture2D(textureSampler, vUV + vec2( 0.0, -1.0) * blurAmount).a;\n    alpha += texture2D(textureSampler, vUV + vec2( 1.0, -1.0) * blurAmount).a;\n\n    alpha += texture2D(textureSampler, vUV + vec2(-1.0,  0.0) * blurAmount).a;\n    alpha += texture2D(textureSampler, vUV + vec2( 0.0,  0.0) * blurAmount).a;\n    alpha += texture2D(textureSampler, vUV + vec2( 1.0,  0.0) * blurAmount).a;\n\n    alpha += texture2D(textureSampler, vUV + vec2(-1.0,  1.0) * blurAmount).a;\n    alpha += texture2D(textureSampler, vUV + vec2( 0.0,  1.0) * blurAmount).a;\n    alpha += texture2D(textureSampler, vUV + vec2( 1.0,  1.0) * blurAmount).a;\n\n    alpha = alpha / 9.0;\n    gl_FragColor = vec4(0.0, 0.0, 0.0, alpha);\n}";
//this shader is based on https://github.com/mrdoob/three.js/blob/dev/examples/js/shaders/BokehShader.js
Effect.ShadersStore["wcf.core.ShadowPlaneDofFragmentShader"] = "\nprecision highp float;\n\nvarying vec2 vUV;\nuniform sampler2D textureSampler;\nuniform float dofAmount;\nuniform float transparency;\n\nvoid main(void)\n{\n    float depth = texture2D( textureSampler, vUV ).x;\n    float dofblur = clamp( depth * dofAmount, 0.0, 1.0 );\n\n    float dofblur9 = dofblur * 0.7; //0.9\n    float dofblur7 = dofblur * 0.5; //0.7\n    float dofblur4 = dofblur * 0.25; //0.4\n\n    float col = 0.0;\n\n    col += depth;\n    col += texture2D( textureSampler, vUV + vec2(  0.0,   0.4  ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.15,  0.37 ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.29,  0.29 ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.37,  0.15 ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.40,  0.0  ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.37, -0.15 ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.29, -0.29 ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.15, -0.37 ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.0,  -0.4  ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.15,  0.37 ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.29,  0.29 ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.37,  0.15 ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.4,   0.0  ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.37, -0.15 ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.29, -0.29 ) * dofblur ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.15, -0.37 ) * dofblur ).x;\n\n    col += texture2D( textureSampler, vUV + vec2(  0.15,  0.37 ) * dofblur9 ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.37,  0.15 ) * dofblur9 ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.37, -0.15 ) * dofblur9 ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.15, -0.37 ) * dofblur9 ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.15,  0.37 ) * dofblur9 ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.37,  0.15 ) * dofblur9 ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.37, -0.15 ) * dofblur9 ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.15, -0.37 ) * dofblur9 ).x;\n\n    col += texture2D( textureSampler, vUV + vec2(  0.29,  0.29 ) * dofblur7 ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.40,  0.0  ) * dofblur7 ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.29, -0.29 ) * dofblur7 ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.0,  -0.4  ) * dofblur7 ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.29,  0.29 ) * dofblur7 ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.4,   0.0  ) * dofblur7 ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.29, -0.29 ) * dofblur7 ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.0,   0.4  ) * dofblur7 ).x;\n\n    col += texture2D( textureSampler, vUV + vec2(  0.29,  0.29 ) * dofblur4 ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.4,   0.0  ) * dofblur4 ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.29, -0.29 ) * dofblur4 ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.0,  -0.4  ) * dofblur4 ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.29,  0.29 ) * dofblur4 ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.4,   0.0  ) * dofblur4 ).x;\n    col += texture2D( textureSampler, vUV + vec2( -0.29, -0.29 ) * dofblur4 ).x;\n    col += texture2D( textureSampler, vUV + vec2(  0.0,   0.4  ) * dofblur4 ).x;\n\n    col = col / 41.0;\n\n    float tra = col + (1.0 - col) * transparency;\n    gl_FragColor = vec4(0.0, 0.0, 0.0, 1.0 - tra);\n}";
